import { createAsyncThunk } from "@reduxjs/toolkit";
import { WaterCharacteristicsPreference } from "@src/core/api/waterCharacteristicsPreferences";
import { BatchSelectionResponse } from "@src/core/engine/workers/batchSelection/schema";
import { RootState } from "@src/core/store";
import { BuildSuggestionOutput } from "@src/routes/projects/$projectId/selections/hooks/useBuildSuggestionOutput";
import { GetGroupedProductDescriptions } from "@src/shared/components/ProductDetailsCard/hooks/useGetProductDescriptions";
import { UnitConversionFn } from "@src/shared/hooks/useUnitConversion";
import { FetchSuggestions } from "../contexts/BatchSuggestionsContext";
import { BatchSelectionState } from "./batchSelectionSlice";
import { buildInput } from "./helpers/buildInput";
import { ensureDataState } from "./helpers/ensureDataState";
import { matchesRequest } from "./helpers/matchesRequest";
import { processRowUpdate } from "./helpers/processRowUpdate";
import { BatchSelectionRow } from "./types";

type ReplaceAllValue = {
  buildSuggestionOutput: BuildSuggestionOutput;
  categoryId: string;
  convertToInternationalUnit: UnitConversionFn;
  convertToUserUnit: UnitConversionFn;
  data: BatchSelectionRow[];
  fetchSuggestions: FetchSuggestions;
  getProductDescriptions: GetGroupedProductDescriptions;
  useFlowOrPower: string;
  waitUntilReady: () => Promise<void>;
  waterCharacteristics: WaterCharacteristicsPreference | null;
};

export const initReplaceAllValues = (
  state: BatchSelectionState,
  args: ReplaceAllValue,
  requestId: string,
) => {
  const nextData = [...args.data];
  nextData.forEach((row, index) => {
    if (!row.output) return;
    nextData[index] = processRowUpdate({
      correlationId: requestId,
      prevRow: undefined,
      row,
      ...args,
    });
  });
  state.data = ensureDataState(nextData);
};

export const replaceAllValues = createAsyncThunk<
  Omit<BatchSelectionResponse, "type">,
  ReplaceAllValue,
  { state: RootState }
>(
  "batchSelection/replaceAllValues",
  async (
    {
      categoryId,
      convertToInternationalUnit,
      fetchSuggestions,
      waitUntilReady,
    },
    { getState, requestId },
  ) => {
    await waitUntilReady();
    const items = getState().batchSelection.data.filter((row) =>
      matchesRequest(row, requestId),
    );
    if (items.length === 0) return { correlationId: requestId, results: [] };

    return fetchSuggestions({
      categoryId,
      correlationId: requestId,
      inputs: items.map((item) => buildInput(item, convertToInternationalUnit)),
    });
  },
);
